/**
 * Static fixtures used by the Quotes shell. No backend yet — these mirror the
 * sample data from the claude.ai/design mockups so the page renders end-to-end
 * for design validation.
 */

// Raw quoterevisions.status is SECURED / UNSECURED; a quote past its End Date
// reads EXPIRED (derived server-side). These are the only display statuses.
export type QuoteStatus = 'SECURED' | 'UNSECURED' | 'EXPIRED';
export type QuoteType = 'WAREHOUSE' | 'DIRECT' | 'INTERMODAL' | 'OTR' | 'BROKERAGE' | 'RAIL' | 'DELIVERY';

/**
 * Shape returned by GET /api/v1/quotes/search and rendered by QuotesTable.
 * Matches the backend's camelCase response one-to-one.
 */
export interface QuoteListRow {
  quoteId: string;
  quoteNumber?: number;
  revisionNumber: number;
  quoteType: QuoteType;
  customer: string;
  /** Stable customer key (masterquote.customercode, int) — pairs with
   *  billToNumber to form the (customer, bill-to) grouping key. */
  customerCode?: number | null;
  billTo: string;
  /** Raw bill-to recno; 0 = deferred "(NEW BILL TO)" (V1 parity). */
  billToNumber?: number;
  /** Customer key-account level (cms.customers.keyaccount via customercode) — Accounts-view column. */
  keyAccount?: string;
  /** Bill-to Sage tag (cms.billtos.mas90code via billto) — Accounts-view column. */
  sageTag?: string;
  lane: string;
  csr: string;
  effective: string;
  expires: string;
  pros: number;
  status: QuoteStatus;
  hot: boolean;
  // P13 #35/N49: extra export columns (hidden-by-default in the grid, exportable).
  businessType?: string;
  deliveryZone?: string;
  createdDate?: string;
  quotedBy?: string;
  soldBy?: string;
  secureDate?: string;
  lastUsedDate?: string;
  daysOut?: number | null;
  // P13 N50: extra-in-rate / combo counts + the "missing options" warning flag.
  extraInRateCnt?: number;
  comboCnt?: number;
  missingExtraInRate?: boolean;
  // Task 5a (Grouped Lane Display): pricing + equipment for the collapsed lane
  // row ("40'HC · Import · Dray $850 · FSC 22% · Total $1,037"). Components only
  // — the backend does NOT compute an all-in total; the shared lane helper
  // (Task 5b) derives the Total from drayWithCharges + FSC, interpreting fscType
  // (percent-of-dray vs flat). DIRECT quotes carry fscRate/fscType (no
  // fuelSurcharge); WAREHOUSE quotes carry the dollar fuelSurcharge (no
  // fscRate/fscType). Empty string when absent for the quote's type.
  moveType?: string;
  size?: string;
  equipType?: string;
  /** Base dray rate (decimal string, "" when absent). */
  drayRate?: string;
  /** Dray + in-rate charges (decimal string). The Total base for the lane row. */
  drayWithCharges?: string;
  /** DIRECT only: FSC rate; interpret with fscType. "" for WAREHOUSE. */
  fscRate?: string;
  /** DIRECT only: FSC interpretation token — one of CURRENT, CUSTOMER, FLAT,
   *  MILES, 'PCT%' (percent-of-dray), DISCOUNTED (or "" / absent). 'FLAT' =
   *  flat dollars. "" for WAREHOUSE. */
  fscType?: string;
  /** WAREHOUSE only: fuel surcharge as a dollar amount. "" for DIRECT. */
  fuelSurcharge?: string;
  /** Per-lane estimated margin % (quoterevisions.estimatedmargin) — sent by the
   *  grouped search; feeds the group-average roll-up. Snake-case (wire). */
  estimatedmargin?: number | null;
  /** Per-lane ACTUAL margin % (intelligence_reportquotes:
   *  (marginincome−marginexpenditure)/marginincome×100) — the grouped Direct
   *  Quotes table's Margin % column. null when no usage row / zero income. */
  actualMargin?: number | null;
  /** v2-migration origin for the optional "Imported" column (flat view). Set/null
   *  by the search enrichment: v2QuoteNumber present = imported from CMSv2;
   *  v2MigrationVerified = a human verified the migrated data in 360. */
  v2QuoteNumber?: number | null;
  v2Lane?: string;
  v2MigrationVerified?: boolean;
}

/**
 * One (customer, bill-to) bucket from the grouped-display search mode
 * (GET /api/v1/quotes/search?group_by=customer_billto). Each `lanes[]` entry is
 * the EXACT same `QuoteListRow` the flat grid returns.
 *
 * Keys mirror the backend's `_shape_groups` output VERBATIM. Note the casing
 * inconsistency: the customer-code key is emitted lowercase as `customercode`
 * while its siblings (`customerName`/`billToNumber`/`billToName`) are camelCase.
 * We type it as-emitted on purpose — the backend is reviewed/frozen, so we match
 * the wire format rather than "fix" it here.
 */
export interface QuoteGroup {
  /** lowercase on the wire (backend `_shape_groups`); siblings are camelCase.
   *  Nullable: the backend passes `masterquote.customercode` through un-coalesced
   *  (matches the lane-level `QuoteListRow.customerCode`). */
  customercode: number | null;
  customerName: string;
  billToNumber: number;
  billToName: string;
  laneCount: number;
  /** No all-in monetary total in the mapped row — backend emits `null`.
   *  Reserved-but-unused slot: never rendered (no authoritative group total
   *  exists yet). Kept on the type because tests reference it — don't hunt for
   *  where it displays. */
  totals: null;
  /** Per-group arithmetic mean of the lanes' economics (#36): the AVERAGE across
   *  the group's lanes of volume / estimatedrevenue / estimatedmargin (the same
   *  quoterevisions columns the lane editor's Fuel & Revenue section edits).
   *  Backend `_shape_groups` skips lanes with no value and emits `null` when NO
   *  lane carries any economics — the header then keeps its "—" placeholder.
   *  Keys mirror the backend VERBATIM (`volume` / `revenue` / `margin`). Each
   *  metric is independently nullable — a metric is `null` when no lane in the
   *  group carries it, even if a sibling metric does (the whole object is `null`
   *  only when ALL three are absent). */
  averages: { volume: number | null; revenue: number | null; margin: number | null } | null;
  /** Year-over-year stats (cms.intelligence_report) for the group's bill-to:
   *  prior + current calendar year volume / revenue / margin%. revenue & margin
   *  are null without ACL_COSTING (same redaction as `averages`). Absent when
   *  the backend didn't attach it. */
  yoy?: {
    prevYear: { volume: number | null; revenue: number | null; margin: number | null };
    currentYear: { volume: number | null; revenue: number | null; margin: number | null };
  } | null;
  /** Per-account status breakdown over ALL current-revision quotes INCLUDING
   *  expired (backend `_attach_group_status_counts`) — expired quotes aren't
   *  loaded as lanes (~100k), so the Status column reads these counts to show
   *  "EXPIRED N". Empty/absent → fall back to counting the loaded lanes. */
  statusCounts?: Partial<Record<QuoteStatus, number>>;
  lanes: QuoteListRow[];
}

/**
 * Response shape of the grouped-display search mode. Paged by GROUP
 * (group_page/group_size), not by row.
 */
export interface GroupedSearchResult {
  totalGroups: number;
  groupPage: number;
  groupSize: number;
  groups: QuoteGroup[];
  /** All-groups mode (group_all=true) CAPS the load to the backend's
   *  GROUP_ALL_CAP (500) groups while still reporting the TRUE `totalGroups`.
   *  `truncated` is true when the load was capped (groups.length < totalGroups) —
   *  the grid then shows a "refine your search" banner. Absent/false for the
   *  paged path and for uncapped all-loads. The client can also infer it from
   *  groups.length < totalGroups; the explicit flag mirrors the backend. */
  truncated?: boolean;
}

/**
 * Static fallback rows. Used by the legacy table-only rendering path (when
 * `rows` aren't passed in) and by component tests.
 */
export const SAMPLE_QUOTES: QuoteListRow[] = [
  { quoteId: 'Q-58812-04', quoteNumber: 58812, revisionNumber: 4, customer: 'DIAGEO NORTH AMERICA',  billTo: 'DIAGEO–GUINNESS USA', quoteType: 'INTERMODAL', lane: 'HOU → DAL', csr: 'C. GOMEZ',   effective: '01/15/2026', expires: '01/14/2027', pros: 184,  status: 'SECURED',   hot: false },
  { quoteId: 'Q-58844-01', quoteNumber: 58844, revisionNumber: 1, customer: 'CONSTELLATION BRANDS',  billTo: 'CONSTELLATION BRANDS', quoteType: 'INTERMODAL', lane: 'LAX → PHX', csr: 'A. CLINGAN', effective: '02/01/2026', expires: '01/31/2027', pros: 42,   status: 'SECURED',   hot: false },
  { quoteId: 'Q-58901-02', quoteNumber: 58901, revisionNumber: 2, customer: 'IKEA U.S. SUPPLY',      billTo: 'IKEA HOLDING US',      quoteType: 'DELIVERY',   lane: 'SAV → ATL', csr: 'J. WALSH',   effective: '—',          expires: '—',          pros: 0,    status: 'UNSECURED',  hot: true  },
  { quoteId: 'Q-58915-00', quoteNumber: 58915, revisionNumber: 0, customer: 'NESTLÉ WATERS N.A.',    billTo: 'NESTLÉ WATERS',        quoteType: 'OTR',        lane: 'CHI → MSP', csr: 'M. STEELE',  effective: '—',          expires: '—',          pros: 0,    status: 'UNSECURED',    hot: false },
  { quoteId: 'Q-57411-09', quoteNumber: 57411, revisionNumber: 9, customer: 'WALMART INC.',          billTo: 'WALMART STORES',       quoteType: 'INTERMODAL', lane: 'LGB → MEM', csr: 'C. GOMEZ',   effective: '06/01/2025', expires: '05/31/2026', pros: 1240, status: 'SECURED', hot: true  },
  { quoteId: 'Q-57102-03', quoteNumber: 57102, revisionNumber: 3, customer: 'KIRIN HOLDINGS (USA)',  billTo: 'KIRIN USA',            quoteType: 'INTERMODAL', lane: 'OAK → SLC', csr: 'A. CLINGAN', effective: '04/12/2025', expires: '04/11/2026', pros: 312,  status: 'SECURED',   hot: false },
  { quoteId: 'Q-56998-06', quoteNumber: 56998, revisionNumber: 6, customer: 'ANHEUSER-BUSCH INBEV',  billTo: 'ANHEUSER-BUSCH',       quoteType: 'BROKERAGE',  lane: 'STL → KC',  csr: 'J. WALSH',   effective: '03/01/2025', expires: '02/28/2026', pros: 88,   status: 'EXPIRED',  hot: false },
  { quoteId: 'Q-58879-00', quoteNumber: 58879, revisionNumber: 0, customer: 'TARGET CORPORATION',    billTo: 'TARGET STORES',        quoteType: 'DELIVERY',   lane: 'LGB → PHX', csr: 'M. STEELE',  effective: '—',          expires: '—',          pros: 0,    status: 'UNSECURED',    hot: false },
];

export interface BillToOption {
  id: string;
  name: string;
  addr1: string;
  addr2: string;
  cityState: string;
}

export const BILL_TO_LIST: BillToOption[] = [
  { id: 'HD-001', name: 'HOME DEPOT — YOUR OTHER WHSE',  addr1: 'ATTN: DAWN BRAZAN',     addr2: '12100 LITTLE CAYMAN, SUITE A', cityState: 'BATON ROUGE, LA  70809' },
  { id: 'DG-008', name: 'DIAGEO–GUINNESS USA',           addr1: 'ATTN: ACCOUNTS PAYABLE', addr2: '801 MAIN AVE',                 cityState: 'NORWALK, CT  06851' },
  { id: 'WM-014', name: 'WALMART STORES',                addr1: 'ATTN: TRANSPORTATION',  addr2: '702 SW 8TH STREET',            cityState: 'BENTONVILLE, AR  72716' },
  { id: 'IK-022', name: 'IKEA HOLDING US',               addr1: 'ATTN: SUPPLY CHAIN',    addr2: '420 ALAN WOOD ROAD',           cityState: 'CONSHOHOCKEN, PA  19428' },
];

export const CUSTOMER_LIST = ['HOME DEPOT', 'DIAGEO NORTH AMERICA', 'WALMART INC.', 'IKEA U.S. SUPPLY'];

/**
 * Shape returned by GET /api/v1/quotes/{n}/{r} and consumed by
 * QuoteDetailPage / QuoteDetailHeader / CustomerInfoTab. Matches the
 * backend's camelCase response one-to-one.
 */
/** One year's roll-up in the Revenue & Statistics "Year over Year" grid. */
export interface QuoteIntelYear {
  year: number;
  volume: number;
  revenue: number;
  margin: number | null;
}

/** Revenue & Statistics intelligence (cms.intelligence_report per bill-to +
 *  cms.intelligence_reportquotes for this revision). */
export interface QuoteIntelligence {
  prevYear: QuoteIntelYear;
  currentYear: QuoteIntelYear;
  revision: { volume: number; revenue: number; margin: number | null } | null;
}

export interface QuoteDetailData {
  // Header / lifecycle
  quoteId: string;
  quoteNumber?: number;
  revisionNumber?: number;
  /** Adjacent existing revisions for the header's ‹Previous / Next› links.
   *  null when this is the first / last revision (link hidden). */
  prevRevision?: number | null;
  nextRevision?: number | null;
  quoteType: QuoteType;
  status: QuoteStatus;
  rawStatus?: string;
  statusDate: string;
  effective: string;
  endDate: string;
  reviewDate: string;
  followUpDate?: string;
  storageLongTermDays?: number | null;
  revReason: string;
  lastUsed: string;
  daysSince: string;
  createDate: string;
  lastChanged?: string;
  lastChangedToken?: string;
  lastChangedBy?: string;
  quotedBy: string;
  soldBy?: string;
  salesRep: string;

  // Bill To card
  billTo: string;
  /** Raw bill-to recno; 0 = deferred "(NEW BILL TO)" (V1 parity). */
  billToNumber?: number;
  sageTag: string;
  addr1: string;
  addr2: string;
  city: string;
  stateCode: string;
  zip: string;
  billFax: string;
  credTerms: string;
  credHold: string;
  arBalance: string;
  billComments: string;

  // Customer card
  customer: string;
  customerCode?: number | null;
  market: string;
  keyAccount: string;
  sfOpp: string;
  /** Salesforce account link (masterquote.salesforcename/salesforcerecno) —
   *  distinct from the free-text sfOpp. recno 0/null = no account linked. */
  salesForceName?: string;
  salesForceRecno?: number | null;
  contact: string;
  phone: string;
  fax: string;
  email: string;
  other: string;
  contractBid: string;
  csr: string;
  salesCsr?: string;
  whseCsr: string;
  fuelCalc: string;
  rateApplies: string;
  /** Legacy "v2" quote cross-reference (display-only). */
  v2QuoteNumber?: number | null;
  v2Lane?: string;
  /** v2-migration state. `v2MigrationTracked` = this tool migrated the quote (a
   *  v2_quote_verification row exists) — the reliable signal for showing the badge,
   *  NOT v2QuoteNumber (which also matches pre-existing v2-linked quotes).
   *  `v2MigrationVerified` = a human reviewed it in 360. */
  v2MigrationTracked?: boolean;
  v2MigrationVerified?: boolean;
  v2MigrationVerifiedBy?: string | null;
  v2MigrationVerifiedDate?: string | null;

  // Revenue / Stats
  volume: string;
  estRev: string;
  estMargin: string;
  internalComments: string;
  /** Year-over-Year (per bill-to) + this-revision stats for the Revenue &
   *  Statistics card. Null/absent when unavailable (e.g. compare view). */
  intelligence?: QuoteIntelligence | null;

  // Type-specific sub-objects from the backend. Exactly one is populated on
  // a healthy row (null on the other half). Both null is also possible when
  // a quote has no type-specific row yet — UI should treat as "—".
  warehouse?: WarehouseFields | null;
  direct?: DirectFields | null;
  /** The lane's CUSTOMER FSC tariff (direct.fscTariff) has no live revision left
   *  in fueldata.fsc_tariff — the fuel link is dead, so nothing resolves at
   *  invoice time. Resolved server-side; absent on the compare view. */
  fscTariffDeleted?: boolean;

  // Child collections from the same endpoint.
  notes?: QuoteNoteRow[];
  /** Active documents on this revision, for the Documents tab badge.
   *  Documents live in a separate service keyed by revisionrecno, so unlike
   *  `notes` they are a count rather than the rows. 0 when none. */
  docCount?: number;
  tariffRates?: TariffRateRow[];
  deliveryLanes?: DeliveryLaneRow[];
  handlingItems?: HandlingItemRow[];
  /** Stopoffs pinned to the directquote row (DIRECT quotes only). */
  directStopoffs?: StopoffRow[];
}

/** One row from quote.deliveryquote with its nested stopoffs. */
export interface DeliveryLaneRow {
  laneId: number;
  warehouse: string;
  deliveryType: string;
  equipType: string;
  deliverToName: string;
  addr1: string;
  addr2: string;
  city: string;
  state: string;
  zip: string;
  miles: string;
  carrierCode: string;
  /** #23 display-only: highway.carrierprofile.truckername for carrierCode.
   *  '' when no carrier or the highway lookup is unavailable. */
  carrierName?: string;
  carrierRate: string;
  carrierQuoteNumber: string;
  deliveryRate: string;
  drayWithCharges: string;
  fscRate: string;
  fscType: string;
  fscTariff: number | null;
  allFscRate: string;
  inDray: YN;
  overloadRate: string;
  overloadType: string;
  waitingTimeFree: string;
  yardStorageFree: number | null;
  /** delratezone — the lane's LOCAL/OTR zone (distinct from deliveryType,
   *  which is V1's FLAT/blank rate-type qualifier). */
  rateZone?: string;
  /** deldelivertocode — cms.locations link, 0/null when free-typed. */
  deliverToCode?: number | null;
  chassisFeeType?: string;
  chassisFeeDays?: number | null;
  lightweightChassisFeeType?: string;
  lightweightChassisFeeDays?: number | null;
  comments: string;
  stopoffs: StopoffRow[];
}

/** One row from quote.stopoffs (deliveryquote-scoped). */
export interface StopoffRow {
  stopId: number;
  stopNum: number;
  stopName: string;
  addr1: string;
  addr2: string;
  city: string;
  state: string;
  zip: string;
  stopType: string;
}

/** One row from quote.handlingquote (WAREHOUSE quotes). handlingType groups
 *  the item into V1's "Handling In" / "Handling Out" lists. */
export interface HandlingItemRow {
  id: number;
  handlingItemRecno: number;
  description: string;
  handlingType: 'handlingin' | 'handlingout' | string;
  rateType: string;
  rate: string;
  unitType: string;
}

/** One row from quote.notes, ordered newest-first by the API. */
export interface QuoteNoteRow {
  noteId: number;
  datetime: string;       // MM/DD/YYYY HH:MM (or "" if missing)
  user: string;
  revisionNumber: number;
  body: string;
}

/** One row from quote.quoteadditionalcharges joined to additionalcharges.
 *  `scope` is the V1 tablename — "transloadquote" / "directquote" /
 *  "deliveryquote" — used by the UI to slot rows onto the right tab. */
export interface TariffRateRow {
  id: number;
  description: string;
  rateType: string;
  rate: string;
  scope: string;
  /** Catalog charge recno (additionalcharges.recno). Present on rows added in the
   *  drawer's create mode so the create payload can reference them; absent on rows
   *  loaded from an existing quote (the detail row carries no catalog recno). */
  additionalChargesRecno?: number;
  /** tablerecno — the table row the charge belongs to (0 otherwise). */
  tableRecno?: number;
  pdfMessage: string;
  /** Per-charge free allowance (display/storage; not invoiced yet). */
  freeTime?: string;
  additionalType?: string | null;
  additionalDays?: number;
  freeTimeUnit?: string;   // 'DAYS' | 'HOURS' | ''
}

export type YN = 'Y' | 'N';

/** transloadquote fields (WAREHOUSE quotes only). Camel-cased to match API. */
export interface WarehouseFields {
  moveType: string;
  accountType: string;
  whseType: string;
  rateZone: string;
  commodity: string;
  packageType: string;
  numUnits: string;
  packagePerContainer: number | null;
  hazmat: YN;
  size: string;
  equipType: string;
  lbs: string;
  kgs: string;
  legalWeight: YN;
  overweight: YN;
  oog: YN;
  triaxle: YN;
  swing: YN;
  chassisType: string;
  numOverflow: number | null;
  perContainers: number | null;
  warehouse: string;
  drayRate: string;
  drayWithCharges: string;
  drayType: string;
  fuelSurcharge: string;
  fuelType: string;
  fuelTariff: number | null;
  drayHandlingCharge: string;
  drayHandlingUnitType: string;
  handlingWithCharges: string;
  includeHandlingInDray: YN;
  storageType: string;
  storageAnniversaryDays: number | null;
  storageRate: string;
  storageRateLongTerm: string;
  storageUnitType: string;
  storageRcvMonthFree: YN;
  storageFreeDays: number | null;
  storageFreePallets: number | null;
  storageDoNotBillReason: string;
  comments: string;
  ess: YN;
  cargoValue: number | null;
  /** OOG spec dims (inches) — V1 specs dialog. */
  lengthInches?: string;
  widthInches?: string;
  heightInches?: string;
  /** Hours of free waiting time. */
  waitingTimeFree?: string;
  /** Days of free yard storage. */
  yardStorageFree?: number | null;
  unitsPerContainer?: number | null;
  useSlidingScale?: YN;
  chassisFeeType?: string;
  chassisFeeDays?: number | null;
  lightweightChassisFeeType?: string;
  lightweightChassisFeeDays?: number | null;
  /** Per-SKU storage/handling overrides from quote.productcoderates. */
  productRates?: ProductRateRow[];
}

/** One row from quote.productcoderates joined to cms.productcodes. */
export interface ProductRateRow {
  id: number;
  sku: string;
  description: string;
  masterUom: string;
  handlingRate: string;
  handlingType: string;
  storageRate: string;
  storageType: string;
}

/** directquote fields (DIRECT quotes only). */
export interface DirectFields {
  moveType: string;
  rateZone: string;
  deliveryZone: string;
  deliverToName: string;
  deliverToAddr1: string;
  deliverToAddr2: string;
  deliverToCity: string;
  deliverToState: string;
  deliverToZip: string;
  deliverToMiles: string;
  size: string;
  equipType: string;
  commodity: string;
  lbs: string;
  kgs: string;
  legalWeight: YN;
  overweight: YN;
  oog: YN;
  triaxle: YN;
  swing: YN;
  hazmat: YN;
  chassisType: string;
  drayRate: string;
  drayWithCharges: string;
  drayType: string;
  fscRate: string;
  fscType: string;
  fscTariff: number | null;
  comments: string;
  ess: YN;
  cargoValue: number | null;
  /** delivertocode — cms.locations link, 0/null when free-typed. */
  deliverToCode?: number | null;
  /** OOG spec dims (inches) — V1 specs dialog. */
  lengthInches?: string;
  widthInches?: string;
  heightInches?: string;
  /** Hours of free waiting time. */
  waitingTimeFree?: string;
  /** Days of free yard storage. */
  yardStorageFree?: number | null;
  chassisFeeType?: string;
  chassisFeeDays?: number | null;
  lightweightChassisFeeType?: string;
  lightweightChassisFeeDays?: number | null;
}

/** A blank directquote sub-object for a brand-new (unsaved) quote — every field
 *  empty / defaulted, flags "N", no location link. Used by the unified create
 *  mode of the quote drawer. */
export function blankDirectFields(): DirectFields {
  return {
    moveType: '', rateZone: '', deliveryZone: '',
    deliverToName: '', deliverToAddr1: '', deliverToAddr2: '',
    deliverToCity: '', deliverToState: '', deliverToZip: '', deliverToMiles: '',
    size: '', equipType: '', commodity: '', lbs: '', kgs: '',
    legalWeight: 'N', overweight: 'N', oog: 'N', triaxle: 'N', swing: 'N', hazmat: 'N',
    chassisType: '', drayRate: '', drayWithCharges: '', drayType: '',
    fscRate: '', fscType: '', fscTariff: null,
    comments: '', ess: 'N', cargoValue: null, deliverToCode: 0,
  };
}

/** A blank DIRECT quote shell for the drawer's create mode: no quote/revision
 *  number (→ (NEW …) badges, editability forced on by the drawer), volume 1
 *  (V1 default), an empty directquote + empty child collections. The drawer
 *  seeds this instead of calling getQuote when opened in create mode. */
export function blankQuoteDetail(): QuoteDetailData {
  return {
    quoteId: '', quoteNumber: undefined, revisionNumber: undefined,
    prevRevision: null, nextRevision: null,
    quoteType: 'DIRECT', status: 'UNSECURED', rawStatus: '',
    statusDate: '', effective: '', endDate: '', reviewDate: '', followUpDate: '',
    revReason: '', lastUsed: '', daysSince: '', createDate: '',
    quotedBy: '', soldBy: '', salesRep: '',
    billTo: '', billToNumber: 0, sageTag: '', addr1: '', addr2: '', city: '',
    stateCode: '', zip: '', billFax: '', credTerms: '', credHold: '', arBalance: '',
    billComments: '',
    customer: '', customerCode: 0, market: '', keyAccount: '', sfOpp: '',
    salesForceName: '', salesForceRecno: null,
    contact: '', phone: '', fax: '', email: '', other: '', contractBid: '',
    csr: '', salesCsr: '', whseCsr: '', fuelCalc: '', rateApplies: '',
    volume: '1', estRev: '', estMargin: '', internalComments: '',
    intelligence: null,
    direct: blankDirectFields(),
    notes: [], tariffRates: [], directStopoffs: [],
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * DIRECT quote creation — POST /quotes payload/result + billto-lookup row.
 * Keys are snake_case to mirror the backend Pydantic models verbatim, so the
 * create wizard can build the request without remapping. Flag fields are
 * plain `string` (not `YN`) because the backend accepts any string and
 * defaults missing/empty values to "N"; the wizard may send "".
 * ────────────────────────────────────────────────────────────────────────── */

/** Request body for POST /api/v1/quotes (create DIRECT quote). */
export interface QuoteCreatePayload {
  header: {
    billto: number;
    billtoname?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    billtofax?: string;
    customercode?: number;
    customername: string;
    marketcode?: number;
    quotetype?: 'DIRECT' | 'WAREHOUSE';
    csr?: string;
    salescsr?: string;
    whsecsr?: string;
    contactname?: string;
    phone?: string;
    fax?: string;
    email?: string;
    /** N6: masterquote.fuelcalctype, seeded from the bill-to's defaultfuelcalc.
     *  Required by the create endpoint — it 422s ("Fuel Calculation is
     *  required.") on a blank. */
    fuelcalctype?: string;
    /** masterquote.rateapplies — which pro date the quote's rates price
     *  against. Required by the create endpoint too ("Rate Applies is
     *  required."); V1 has no bill-to default for it. */
    rateapplies?: string;
    /** masterquote.reviewdate (ISO YYYY-MM-DD). Required by the create endpoint —
     *  it 422s ("Review Date is required.") on a blank/zero value. */
    reviewdate?: string;
    /** Free-text Salesforce opportunity (SF Opp) — masterquote.salesforceopportunityid. */
    salesforceopportunityid?: string;
  };
  revision?: {
    soldby?: string;
    status?: string;
    businesstype?: string;
    effectivedate?: string | null;
    enddate?: string | null;
    securedate?: string | null;
    volume?: number;
    volumetype?: string;
    /** quoterevisions.estimatedrevenue — auto-calc (dray + FSC adj) × volume. */
    estimatedrevenue?: number;
    /** quoterevisions.estimatedmargin — manual % (V1 has no margin auto-calc). */
    estimatedmargin?: number;
  };
  /** Present for DIRECT quotes (XOR with `transload`, gated by header.quotetype). */
  direct?: {
    movetype: string;
    ratezone?: string;
    deliveryzone?: string;
    delivertoname?: string;
    delivertoaddr1?: string;
    delivertoaddr2?: string;
    delivertocity?: string;
    delivertostate?: string;
    delivertozip?: string;
    delivertomiles?: number;
    /** cms.locations link from the location picker; 0 = free-typed. */
    delivertocode?: number;
    size: string;
    equiptype?: string;
    commodity?: string;
    lbs?: number;
    kgs?: number;
    legalweight?: string;
    overweight?: string;
    oog?: string;
    triaxle?: string;
    swing?: string;
    hazmat?: string;
    chassistype?: string;
    drayrate?: number;
    draywithcharges?: number;
    draytype?: string;
    fscrate?: number;
    fsctype?: string;
    fsctariff?: number;
    comments?: string;
    hvc?: string;
    cargovalue?: number;
    waitingtimefree?: number;
    yardstoragefree?: number;
    chassisfeetype?: string;
    chassisfeedays?: number;
    lightweightchassisfeetype?: string;
    lightweightchassisfeedays?: number;
  };
  /** Present for WAREHOUSE quotes (XOR with `direct`). transloadquote columns. */
  transload?: {
    lengthinches?: number;
    widthinches?: number;
    heightinches?: number;
    numoverflow?: number;
    percontainers?: number;
    waitingtimefree?: number;
    yardstoragefree?: number;
    unitspercontainer?: number;
    chassisfeetype?: string;
    chassisfeedays?: number;
    lightweightchassisfeetype?: string;
    lightweightchassisfeedays?: number;
    movetype: string;
    size: string;
    whsetype?: string;
    accounttype?: string;
    packagetype?: string;
    numunits?: string;
    packagepercontainer?: number;
    ratezone?: string;
    commodity?: string;
    equiptype?: string;
    hazmat?: string;
    legalweight?: string;
    overweight?: string;
    oog?: string;
    triaxle?: string;
    swing?: string;
    chassistype?: string;
    warehouse?: string;
    lbs?: number;
    kgs?: number;
    drayrate?: number;
    draytype?: string;
    fuelsurcharge?: number;
    fueltype?: string;
    fueltariff?: number;
    drayhandlingcharge?: number;
    drayhandlingunittype?: string;
    includehandlingindray?: string;
    storagetype?: string;
    storageanniversarydays?: number;
    storagerate?: number;
    storageratelongterm?: number;
    storageunittype?: string;
    storagercvmonthfree?: string;
    storagefreedays?: number;
    storagefreepallets?: number;
    storagedonotbillreason?: string;
    useslidingscale?: string;
    comments?: string;
    hvc?: string;
    cargovalue?: number;
  };
  /** WAREHOUSE delivery lanes (deliveryquote del* columns; create-time subset). */
  lanes?: Array<{
    delwarehouse?: string;
    delratezone?: string;
    delequiptype?: string;
    /** cms.locations link when picked from the lookup; 0/absent when free-typed. */
    deldelivertocode?: number;
    deldelivertoname?: string;
    deladdr1?: string;
    deladdr2?: string;
    delcity?: string;
    delstate?: string;
    delzip?: string;
    delmiles?: number;
    delcarriercode?: string;
    deldeliveryrate?: number;
    deldeliverytype?: string;
    delfscrate?: number;
    delfsctype?: string;
    delindray?: string;
    delcomments?: string;
  }>;
  /** WAREHOUSE handling items (handlingquote). */
  handling?: Array<{
    handlingitemrecno: number;
    handlingtype?: 'handlingin' | 'handlingout';
    handlingratetype?: string;
    handlingrate?: number;
    handlingunittype?: string;
  }>;
  tariffRates?: Array<{
    additionalchargesrecno?: number;
    addlchargeratetype?: string;
    addlchargerate?: number;
    freetime?: number;
    freetimeunit?: string;
    additionaltype?: string;
    additionaldays?: number;
  }>;
  notes?: Array<{ notes?: string }>;
}

/** Response from POST /api/v1/quotes. */
export interface QuoteCreateResult {
  quoteNumber: number;
  revisionNumber: number;
  quoteId: string;
  /** Descriptions of catalog charges skipped on copy because they were deleted
   *  from the additionalcharges catalog (create-revision / dupe only). */
  droppedCharges?: string[];
  /** Non-blocking save notices — the departed-staff rule (an LC / Whse CSR /
   *  bill-to sales rep who no longer works here) for a non-LC saver. An LC
   *  saver gets a 422 instead. Shown in the drawer's amber banner. */
  warnings?: string[];
}

/** Request body for PATCH /api/v1/quotes/{n}/{r} (edit DIRECT quote). All
 *  sub-objects and their fields are optional — send only what changed.
 *  `expectedLastChanged` carries the raw `lastChangedToken` from the GET
 *  response for optimistic-concurrency checking. Keys are snake_case to
 *  mirror the backend QuoteUpdate*In models verbatim. */
export interface QuoteUpdatePayload {
  expectedLastChanged: string;
  header?: {
    billto?: number; billtoname?: string;
    address1?: string; address2?: string; city?: string; state?: string; zip?: string;
    billtofax?: string; customercode?: number; customername?: string; marketcode?: number;
    csr?: string; salescsr?: string; whsecsr?: string;
    contactname?: string; phone?: string; fax?: string; email?: string;
    salesforceopportunityid?: string; salesforcename?: string; salesforcerecno?: number;
    other?: string; contractnumber?: string;
    fuelcalctype?: string; rateapplies?: string; mastercomments?: string;
    reviewdate?: string; followupdate?: string; storagelongtermdays?: number;
  };
  revision?: {
    status?: string; businesstype?: string;
    effectivedate?: string; enddate?: string; securedate?: string;
    volume?: number; volumetype?: string; soldby?: string;
    estimatedrevenue?: number; estimatedmargin?: number;
  };
  direct?: {
    movetype?: string; ratezone?: string; deliveryzone?: string;
    delivertoname?: string; delivertoaddr1?: string; delivertoaddr2?: string;
    delivertocity?: string; delivertostate?: string; delivertozip?: string;
    delivertomiles?: number; size?: string; equiptype?: string; commodity?: string;
    lbs?: number; kgs?: number;
    legalweight?: string; overweight?: string; oog?: string; triaxle?: string; swing?: string; hazmat?: string;
    chassistype?: string; drayrate?: number; draywithcharges?: number; draytype?: string;
    fscrate?: number; fsctype?: string; fsctariff?: number;
    comments?: string; hvc?: string; cargovalue?: number;
    delivertocode?: number;
    lengthinches?: number; widthinches?: number; heightinches?: number;
    waitingtimefree?: number; yardstoragefree?: number;
    chassisfeetype?: string; chassisfeedays?: number;
    lightweightchassisfeetype?: string; lightweightchassisfeedays?: number;
  };
  transload?: {
    movetype?: string; accounttype?: string; whsetype?: string; ratezone?: string;
    commodity?: string; packagetype?: string; numunits?: string; packagepercontainer?: number;
    hazmat?: string; size?: string; equiptype?: string; lbs?: number; kgs?: number;
    legalweight?: string; overweight?: string; oog?: string; triaxle?: string; swing?: string;
    chassistype?: string; numoverflow?: number; percontainers?: number; warehouse?: string;
    drayrate?: number; draywithcharges?: number; draytype?: string;
    fuelsurcharge?: number; fueltype?: string; fueltariff?: number;
    drayhandlingcharge?: number; drayhandlingunittype?: string; handlingwithcharges?: number;
    unitspercontainer?: number; includehandlingindray?: string;
    storagetype?: string; storageanniversarydays?: number; storagerate?: number;
    storageratelongterm?: number; storageunittype?: string; storagercvmonthfree?: string;
    storagefreedays?: number; storagefreepallets?: number; storagedonotbillreason?: string;
    comments?: string; hvc?: string; cargovalue?: number;
    useslidingscale?: string; yardstoragefree?: number;
    lengthinches?: number; widthinches?: number; heightinches?: number; waitingtimefree?: number;
    chassisfeetype?: string; chassisfeedays?: number;
    lightweightchassisfeetype?: string; lightweightchassisfeedays?: number;
  };
}

/** One row from GET /api/v1/quotes/billto-lookup. Mixed key casing mirrors
 *  the backend response exactly (no customercode/marketcode — those don't
 *  exist on cms.billtos). `arBalance` is a decimal string like "1290.00" or "". */
export interface BillToLookupRow {
  billto: number;
  billtoname: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  billtofax: string;
  salescsr: string;
  keyAccount: string;
  creditTerms: string;
  creditHold: string;
  mas90code: string;
  arBalance: string;
  /** N6 — cms.billtos.defaultfuelcalc; seeds the quote's fuelcalctype on pick. */
  defaultFuelCalc: string;
}

/** One row from GET /api/v1/quotes/customer-lookup (create wizard customer
 *  picker). `marketcode` is null when the customer has no market set. */
export interface CustomerLookupRow {
  customercode: number;
  custname: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  marketcode: number | null;
  market: string;
  keyAccount: string;
  /** The customer's sales-rep username (cms.customers.salesrep). */
  salesrep: string;
  /** The rep's resolved display name (gwi.users first/last), "" when unmatched. */
  salesRepName?: string;
}

/** GET /api/v1/quotes/customer-form-options — New Customer modal dropdowns. */
export interface CustomerFormOptions {
  markets: { recno: number; market: string }[];
  salesReps: { username: string; displayname: string }[];
  csrs: { username: string; displayname: string }[];
  keyAccounts: string[];
  regions: string[];
  communication: string[];
}

/** Request body for POST /api/v1/quotes/customers. Mirrors the backend
 *  CustomerCreateIn — only custname + marketcode are required; the rest have
 *  the V1 editcustomers new-record defaults server-side. */
export interface CustomerCreatePayload {
  custname: string;
  marketcode: number;
  custadd?: string; custadd2?: string; custcity?: string; custstate?: string;
  custzip?: string; custphone?: string; custfax?: string; communication?: string;
  keyaccount?: string; salesrep?: string; logisticscoordinator?: string;
  brokeragecsr?: string; custtype?: 'MASTER' | 'DIVISION'; masterrecno?: number;
  salesforceid?: string; custregion?: string; allowbrokerage?: string;
  complexityscore?: number;
  showcustomerco?: string; customlfd?: string; sealverification?: string;
  hidecustomerlabel?: string; custombl?: string;
  showrcvbol?: string; showrcvequipment?: string; showrcvpo?: string;
  showrcvseal?: string; showlotnum?: string; showserialnum?: string;
  showexpiration?: string; showmanufacture?: string;
  additionalinfo?: string; shippinginstructions?: string;
}

/** Response from POST /api/v1/quotes/customers. */
export interface CustomerCreateResult {
  customercode: number;
  custname: string;
  marketcode: number;
}

/** Typed "New Bill To" form (V1 inline_quote.pl field set). The quote app never
 *  inserts a cms.billtos row — a new bill-to defers to billto=0 with these values
 *  carried on the quote's own columns (see
 *  docs/plans/2026-06-17-quotes-deferred-billto-design.md). No API request body. */
export interface NewBillToForm {
  name: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  contact: string;
  phone: string;
  fax: string;
}

/** One row from GET /api/v1/quotes/location-lookup (create wizard delivery-
 *  location picker). Lookup only — location creation lives on the locations
 *  page, not in the quote wizard. */
export interface LocationLookupRow {
  locationcode: number;
  lname: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  cyalias: string;
  locationtype: string;
}

/** One row from GET /quotes/salesforce-lookup (non-deleted quote.salesforce). */
export interface SalesforceLookupRow {
  salesforcerecno: number;
  name: string;
  city: string;
  state: string;
}

export const SAMPLE_WAREHOUSE_QUOTE_DETAIL: QuoteDetailData = {
  quoteId: '43448-11',
  quoteNumber: 43448,
  revisionNumber: 11,
  quoteType: 'WAREHOUSE',
  reviewDate: '12/14/2022',
  effective: '09/15/2022',
  endDate: '',
  status: 'SECURED',
  statusDate: '09/15/2022',
  revReason: 'RATE REVISION',
  lastUsed: '05/14/2026',
  daysSince: '3',
  quotedBy: 'aelizondo',
  salesRep: 'Abby Elizondo',
  createDate: '09/15/2022 14:34',

  billTo: 'STEELEX INC.',
  sageTag: 'STEELEX',
  addr1: '656 CENTRAL PARK AVE',
  addr2: 'SUITE 120',
  city: 'SCARSDALE',
  stateCode: 'NY',
  zip: '10583',
  billFax: '',
  credTerms: 'net 30',
  credHold: 'N',
  arBalance: '1290.00',
  billComments: '',

  customer: 'STEELEX INC',
  market: 'INDUSTRIAL PRODUCTS',
  keyAccount: 'AVERAGE',
  sfOpp: '',
  contact: 'INTERNAL',
  phone: '',
  fax: '',
  email: '',
  other: '',
  contractBid: '',
  csr: 'SABRINA OROZCO (155)',
  whseCsr: 'SABRINA OROZCO (155)',
  fuelCalc: 'DELIVERY DATE',
  rateApplies: 'ON DELIVERY',

  volume: '1',
  estRev: '1701.50',
  estMargin: '0.00',
  internalComments: '',

  notes: [
    { noteId: 248092, datetime: '09/15/2022 15:47', user: 'sorozco',     revisionNumber: 11, body: 'Quote emailed to sorozco@gwii.com.' },
    { noteId: 248090, datetime: '09/15/2022 14:35', user: 'aelizondo',   revisionNumber: 11, body: 'Duped from Quote 43448-10' },
    { noteId: 239878, datetime: '06/25/2021 12:22', user: 'bcovarrubias', revisionNumber: 10, body: 'FRESHDESK INTERNAL QUOTE - ADDL CHARGE REQUEST\nJENNIFER ROGERSON REPORTED VIA EMAIL\nTO: PRICING INBOX, SABRINA OROZCO, HEATHER OVERBY\nPLEASE ADD $1050 DELIVERY CHARGE FOR FLATBED (NO TARP) TO DALLAS TO QUOTE 43448.' },
  ],
  tariffRates: [
    { id: 2005846, description: 'CHASSIS FEE',              rateType: 'EXTRA', rate: '90.00',  scope: 'transloadquote', pdfMessage: 'per day' },
    { id: 2088332, description: 'CHASSIS SPLIT',            rateType: 'EXTRA', rate: '95.00',  scope: 'transloadquote', pdfMessage: 'per split' },
    { id: 2088333, description: 'EQUIPMENT REPOSITION FEE', rateType: 'EXTRA', rate: '100.00', scope: 'transloadquote', pdfMessage: '' },
  ],
  deliveryLanes: [
    { laneId: 55831, warehouse: 'BARBOURS CUT/BAYPORT', deliveryType: '', equipType: 'FLATBED', deliverToName: '', addr1: '', addr2: '', city: 'DALLAS',       state: 'TX', zip: '75201', miles: '269.420', carrierCode: '', carrierRate: '0.00', carrierQuoteNumber: '', deliveryRate: '1050.00', drayWithCharges: '0.00', fscRate: '0.000',  fscType: '',           fscTariff: null, allFscRate: '0.000', inDray: 'N', overloadRate: '0.00', overloadType: '', waitingTimeFree: '0', yardStorageFree: 0, comments: '', stopoffs: [] },
    { laneId: 55832, warehouse: 'BARBOURS CUT/BAYPORT', deliveryType: '', equipType: 'VAN',     deliverToName: '', addr1: '', addr2: '', city: 'AUSTIN',       state: 'TX', zip: '78767', miles: '194.000', carrierCode: '', carrierRate: '0.00', carrierQuoteNumber: '', deliveryRate: '650.00',  drayWithCharges: '0.00', fscRate: '0.000',  fscType: '',           fscTariff: null, allFscRate: '0.000', inDray: 'N', overloadRate: '0.00', overloadType: '', waitingTimeFree: '0', yardStorageFree: 0, comments: '', stopoffs: [] },
    { laneId: 58463, warehouse: 'BARBOURS CUT/BAYPORT', deliveryType: 'LOCAL', equipType: 'VAN', deliverToName: '', addr1: '', addr2: '', city: 'FRIENDSWOOD', state: 'TX', zip: '77546', miles: '23.680',  carrierCode: '', carrierRate: '0.00', carrierQuoteNumber: '', deliveryRate: '330.00',  drayWithCharges: '0.00', fscRate: '68.000', fscType: 'GULF WINDS', fscTariff: null, allFscRate: '0.000', inDray: 'N', overloadRate: '0.00', overloadType: '', waitingTimeFree: '0', yardStorageFree: 0, comments: '', stopoffs: [] },
  ],

  warehouse: {
    moveType: 'IMPORT',
    accountType: 'TRANSLOAD',
    whseType: 'FULL STRIP/STUFF',
    rateZone: 'BBC / BAYPORT',
    commodity: 'PALLETIZED CARGO',
    packageType: 'UNITIZED < 8,000 LBS',
    numUnits: '',
    packagePerContainer: null,
    hazmat: 'N',
    size: '20/40',
    equipType: 'DRY',
    lbs: '0',
    kgs: '0',
    legalWeight: 'Y',
    overweight: 'N',
    oog: 'N',
    triaxle: 'N',
    swing: 'N',
    chassisType: '',
    numOverflow: 0,
    perContainers: 0,
    warehouse: 'BARBOURS CUT/BAYPORT',
    drayRate: '75.00',
    drayWithCharges: '75.00',
    drayType: '',
    fuelSurcharge: '',
    fuelType: 'FLAT',
    fuelTariff: null,
    drayHandlingCharge: '425.00',
    drayHandlingUnitType: 'CONTAINER',
    handlingWithCharges: '425.00',
    includeHandlingInDray: 'Y',
    storageType: 'ANNIVERSARY',
    storageAnniversaryDays: 7,
    storageRate: '6.50',
    storageRateLongTerm: '0.00',
    storageUnitType: 'PLT',
    storageRcvMonthFree: 'N',
    storageFreeDays: 10,
    storageFreePallets: 0,
    storageDoNotBillReason: '',
    comments: '',
    ess: 'N',
    cargoValue: 0,
    productRates: [
      { id: 10039, sku: 'STEEL FENCE ITEMS - BUNDLES', description: '', masterUom: 'BUNDLE', handlingRate: '0.00', handlingType: '', storageRate: '6.50', storageType: 'BUNDLE' },
    ],
  },
  direct: null,
};

export interface DeliveryLane {
  whse: string;
  type: string;
  equip: string;
  name: string;
  addr: string;
  city: string;
  state: string;
  zip: string;
  miles: string;
  carrier: string;
  dtd: string;
  rate: string;
  delivFsc?: string;
  delivFscZone?: string;
}

export const SAMPLE_QUOTE_DETAIL: QuoteDetailData = {
  quoteId: '43448-11',
  quoteNumber: 43448,
  revisionNumber: 11,
  quoteType: 'DIRECT',
  reviewDate: '12/14/2022',
  effective: '09/15/2022',
  endDate: '',
  status: 'SECURED',
  statusDate: '09/15/2022',
  revReason: 'RATE REVISION',
  lastUsed: '05/14/2026',
  daysSince: '3',
  quotedBy: 'aelizondo',
  salesRep: 'Abby Elizondo',
  createDate: '09/15/2022 14:34',

  billTo: 'STEELEX INC.',
  sageTag: 'STEELEX',
  addr1: '656 CENTRAL PARK AVE',
  addr2: 'SUITE 120',
  city: 'SCARSDALE',
  stateCode: 'NY',
  zip: '10583',
  billFax: '',
  credTerms: 'net 30',
  credHold: 'N',
  arBalance: '1290.00',
  billComments: '',

  customer: 'STEELEX INC',
  market: 'INDUSTRIAL PRODUCTS',
  keyAccount: 'AVERAGE',
  sfOpp: '',
  contact: 'INTERNAL',
  phone: '',
  fax: '',
  email: '',
  other: '',
  contractBid: '',
  csr: 'SABRINA OROZCO (155)',
  whseCsr: 'SABRINA OROZCO (155)',
  fuelCalc: 'DELIVERY DATE',
  rateApplies: 'ON DELIVERY',

  volume: '1',
  estRev: '1701.50',
  estMargin: '0.00',
  internalComments: '',

  notes: [
    { noteId: 248092, datetime: '09/15/2022 15:47', user: 'sorozco',     revisionNumber: 11, body: 'Quote emailed to sorozco@gwii.com.' },
    { noteId: 248090, datetime: '09/15/2022 14:35', user: 'aelizondo',   revisionNumber: 11, body: 'Duped from Quote 43448-10' },
    { noteId: 239878, datetime: '06/25/2021 12:22', user: 'bcovarrubias', revisionNumber: 10, body: 'FRESHDESK INTERNAL QUOTE - ADDL CHARGE REQUEST\nJENNIFER ROGERSON REPORTED VIA EMAIL\nTO: PRICING INBOX, SABRINA OROZCO, HEATHER OVERBY\nPLEASE ADD $1050 DELIVERY CHARGE FOR FLATBED (NO TARP) TO DALLAS TO QUOTE 43448.' },
  ],
  tariffRates: [
    { id: 2005846, description: 'CHASSIS FEE',              rateType: 'EXTRA', rate: '90.00',  scope: 'directquote', pdfMessage: 'per day' },
    { id: 2088332, description: 'CHASSIS SPLIT',            rateType: 'EXTRA', rate: '95.00',  scope: 'directquote', pdfMessage: 'per split' },
    { id: 2088333, description: 'EQUIPMENT REPOSITION FEE', rateType: 'EXTRA', rate: '100.00', scope: 'directquote', pdfMessage: '' },
  ],
  directStopoffs: [],

  direct: {
    moveType: 'IMPORT',
    rateZone: 'BBC / BAYPORT',
    deliveryZone: 'OTR',
    deliverToName: '',
    deliverToAddr1: '',
    deliverToAddr2: '',
    deliverToCity: 'DALLAS',
    deliverToState: 'TX',
    deliverToZip: '75201',
    deliverToMiles: '269.420',
    size: '20/40',
    equipType: 'DRY',
    commodity: 'PALLETIZED CARGO',
    lbs: '0',
    kgs: '0',
    legalWeight: 'Y',
    overweight: 'N',
    oog: 'N',
    triaxle: 'N',
    swing: 'N',
    hazmat: 'N',
    chassisType: '',
    drayRate: '75.00',
    drayWithCharges: '75.00',
    drayType: '',
    fscRate: '0.000',
    fscType: '',
    fscTariff: null,
    comments: '',
    ess: 'N',
    cargoValue: 0,
    deliverToCode: 0,
  },
};

export interface QuoteNote {
  date: string;
  user: string;
  rev: string;
  body: string;
  highlight?: boolean;
}

export const SAMPLE_DELIVERY_LANES: DeliveryLane[] = [
  { whse: 'BARBOURS CUT/BAYPORT', type: 'LOCAL', equip: 'VAN',     name: '', addr: '', city: 'FRIENDSWOOD', state: 'TX', zip: '77546', miles: '23.68', carrier: '', dtd: 'N', rate: '330.00', delivFsc: '68.0', delivFscZone: 'GULF WINDS' },
  { whse: 'BARBOURS CUT/BAYPORT', type: 'OTR',   equip: 'FLATBED', name: '', addr: '', city: 'DALLAS',      state: 'TX', zip: '75201', miles: '',      carrier: '', dtd: 'N', rate: '1050.00' },
  { whse: 'BARBOURS CUT/BAYPORT', type: 'OTR',   equip: 'VAN',     name: '', addr: '', city: 'AUSTIN',      state: 'TX', zip: '78767', miles: '',      carrier: '', dtd: 'N', rate: '650.00' },
  { whse: 'BARBOURS CUT/BAYPORT', type: 'OTR',   equip: 'FLATBED', name: '', addr: '', city: 'DALLAS',      state: 'TX', zip: '75237', miles: '',      carrier: '', dtd: 'N', rate: '975.00' },
  { whse: 'BARBOURS CUT/BAYPORT', type: 'OTR',   equip: 'VAN',     name: '', addr: '', city: 'DALLAS',      state: 'TX', zip: '75237', miles: '',      carrier: '', dtd: 'N', rate: '550.00' },
  { whse: 'BARBOURS CUT/BAYPORT', type: 'OTR',   equip: 'VAN',     name: '', addr: '', city: 'SAN ANTONIO', state: 'TX', zip: '78265', miles: '',      carrier: '', dtd: 'N', rate: '725.00' },
];

export interface QuoteNote {
  date: string;
  user: string;
  rev: string;
  body: string;
  highlight?: boolean;
}

export const SAMPLE_QUOTE_NOTES: QuoteNote[] = [
  { date: '05/17/2026 14:36:49', user: 'jduplechain', rev: '11', body: '', highlight: true },
  { date: '09/15/2022 15:47:46', user: 'sorozco',     rev: '11', body: 'Quote emailed to sorozco@gwii.com.' },
  { date: '09/15/2022 14:35:12', user: 'aelizondo',   rev: '11', body: 'Duped from Quote 43448-10' },
  {
    date: '06/25/2021 12:22:28', user: 'bcovarrubias', rev: '10',
    body: `FRESHDESK INTERNAL QUOTE - ADDL CHARGE REQUEST

JENNIFER ROGERSON REPORTED VIA EMAIL
2 HOURS AGO (FRI, 25 JUN 2021 AT 10:41 AM)

TO:"PRICING INBOX" <PRICING@GWII.COM>, "SABRINA OROZCO" <SOROZCO@GWII.COM>, "HEATHER OVERBY" <HOVERBY@GWII.COM>
HELLO,

PLEASE ADD __$1050 DELIVERY CHARGE FOR FLATBED (NO TARP) TO DALLAS ______ TO QUOTE _43448____.`,
  },
  {
    date: '05/03/2019 11:21:56', user: 'bcovarrubias', rev: '10',
    body: `HI BRITNEY COVARRUBIAS,

$975 ALL IN

ON FRI, 3 MAY AT 10:05 AM , BRITNEY COVARRUBIAS <BCOVARRUBIAS@GWII.COM> WROTE:
JOSHUA JOHNSON
REPORTED VIA EMAIL, 3 HOURS AGO (FRI, 3 MAY 2019 AT 8:21 AM)
TO:
"PRICING INBOX" <PRICING@GWII.COM>`,
  },
];
